import React, { useMemo, useState } from 'react';

import TextField from '@mui/material/TextField';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';

const ANNOTATION_PREFIX = 'localStorage://?canvasId=';
const SEARCH_QUERY_KEY = 'mirador-local-annotation-search';

/*
 * ------------------------------------------------------------
 * Extract searchable text
 * ------------------------------------------------------------
 */

function stripHtml(value) {
    if (!value) {
        return '';
    }

    const div = document.createElement('div');
    div.innerHTML = String(value);

    return div.textContent || div.innerText || '';
}


function getAnnotationText(annotation) {
    const values = [];

    const body = annotation?.body;

    if (typeof body === 'string') {
        values.push(body);
    }

    if (Array.isArray(body)) {
        body.forEach((item) => {
            if (typeof item === 'string') {
                values.push(item);
            }

            if (item?.value) {
                values.push(item.value);
            }

            if (item?.chars) {
                values.push(item.chars);
            }

            if (item?.label) {
                values.push(item.label);
            }
        });
    }

    if (body?.value) {
        values.push(body.value);
    }

    if (body?.chars) {
        values.push(body.chars);
    }

    if (body?.label) {
        values.push(body.label);
    }

    if (annotation?.label) {
        values.push(annotation.label);
    }

    if (Array.isArray(annotation?.tags)) {
        values.push(annotation.tags.join(' '));
    }

    return values
        .map(stripHtml)
        .join(' ')
        .replace(/\s+/g, ' ')
        .trim();
}


/*
 * ------------------------------------------------------------
 * Read annotations from localStorage
 * ------------------------------------------------------------
 */

function readAnnotations() {
    const results = [];

    for (let i = 0; i < localStorage.length; i += 1) {
        const key = localStorage.key(i);

        if (!key || !key.startsWith(ANNOTATION_PREFIX)) {
            continue;
        }

        const canvasId =
            key.substring(ANNOTATION_PREFIX.length);

        try {
            const data = JSON.parse(
                localStorage.getItem(key)
            );

            if (!Array.isArray(data?.items)) {
                continue;
            }

            data.items.forEach((annotation, index) => {
                results.push({
                    key,
                    index,
                    canvasId,
                    annotation,
                    text: getAnnotationText(annotation),
                });
            });
        } catch (error) {
            console.warn(
                'Could not read annotation:',
                key,
                error
            );
        }
    }

    return results;
}

/*
 * ------------------------------------------------------------
 * Scope current manifest canvases
 * ------------------------------------------------------------
 */

function getCurrentManifestCanvasIds(windowId) {
    const mirador = window.miradorInstance;

    if (!mirador || !windowId) {
        return new Set();
    }

    const state = mirador.store.getState();
    const windowState = state.windows?.[windowId];

    if (!windowState?.manifestId) {
        return new Set();
    }

    const manifestId = windowState.manifestId;
    const manifestState = state.manifests?.[manifestId];

    // Mirador normally stores the IIIF manifest here
    const manifest =
        manifestState?.json ||
        manifestState?.manifest ||
        manifestState;

    if (!manifest) {
        return new Set();
    }

    const canvasIds = new Set();

    // IIIF Presentation 3
    if (Array.isArray(manifest.items)) {
        manifest.items.forEach((canvas) => {
            const id = canvas?.id || canvas?.['@id'];

            if (id) {
                canvasIds.add(id);
            }
        });
    }

    // IIIF Presentation 2
    if (Array.isArray(manifest.sequences)) {
        manifest.sequences.forEach((sequence) => {
            if (!Array.isArray(sequence.canvases)) {
                return;
            }

            sequence.canvases.forEach((canvas) => {
                const id = canvas?.id || canvas?.['@id'];

                if (id) {
                    canvasIds.add(id);
                }
            });
        });
    }

    return canvasIds;
}

/*
 * ------------------------------------------------------------
 * Search component
 * ------------------------------------------------------------
 */

function LocalStorageSearch({
    windowId,
    setCanvas,
}) {
    const [query, setQuery] = useState(
        () => sessionStorage.getItem(SEARCH_QUERY_KEY) || ''
    );
    const annotations = useMemo(
        () => {
            const allAnnotations = readAnnotations();

            const manifestCanvasIds =
                getCurrentManifestCanvasIds(windowId);

            return allAnnotations.filter(
                (item) =>
                    manifestCanvasIds.has(item.canvasId)
            );
        },
        [windowId]
    );

    const normalizedQuery =
        query.trim().toLowerCase();

    const results = normalizedQuery
        ? annotations.filter((item) =>
            item.text
                .toLowerCase()
                .includes(normalizedQuery)
        )
        : [];

    const handleResultClick = (result) => {
        const mirador = window.miradorInstance;
        const actions = window.miradorActions;

        if (!mirador || !actions || !windowId || !result.canvasId) {
            return;
        }

        const annotationId =
            result.annotation?.id ||
            result.annotation?.['@id'];

        if (!annotationId) {
            return;
        }

        const dispatch = mirador.store.dispatch;

        // 1. Move to the annotation's canvas
        dispatch(
            actions.setCanvas(
                windowId,
                result.canvasId
            )
        );

        // 2. Remove the custom search companion
        const state = mirador.store.getState();
        const windowState = state.windows[windowId];

        const customCompanionId =
            windowState.companionWindowIds?.find(
                (id) =>
                    state.companionWindows[id]?.content === 'custom'
            );

        if (customCompanionId) {
            dispatch(
                actions.removeCompanionWindow(
                    windowId,
                    customCompanionId
                )
            );
        }

        // 3. Add/open the native Annotations companion
        dispatch(
            actions.addCompanionWindow(
                windowId,
                {
                    content: 'annotations',
                    position: 'left',
                }
            )
        );

        // 4. Select and highlight the annotation
        setTimeout(() => {
            dispatch(
                actions.selectAnnotation(
                    windowId,
                    annotationId
                )
            );

            dispatch(
                actions.hoverAnnotation(
                    windowId,
                    [annotationId]
                )
            );
        }, 500);
    };

        return (
        <div
            style={{
                padding: '12px',
                width: '100%',
                boxSizing: 'border-box',
            }}
        >
            <TextField
                fullWidth
                autoFocus
                size="small"
                label="Search annotations"
                placeholder="Enter search text..."
                value={query}
                onChange={(event) => {
                    const value = event.target.value;

                    setQuery(value);
                    sessionStorage.setItem(
                        SEARCH_QUERY_KEY,
                        value
                    );
                }}
            />
            {!normalizedQuery && (
                <Typography
                    variant="body2"
                    sx={{
                        mt: 2,
                        color: 'text.secondary',
                    }}
                >
                    Search annotations stored in
                    browser localStorage.
                </Typography>
            )}

            {normalizedQuery &&
                results.length === 0 && (
                    <Typography
                        variant="body2"
                        sx={{
                            mt: 2,
                            color: 'text.secondary',
                        }}
                    >
                        No annotations found.
                    </Typography>
                )}

            {results.length > 0 && (
                <List dense>
                    {results.map((result) => (
                        <ListItemButton
                            key={`${result.key}-${result.index}`}
                            onClick={() =>
                                handleResultClick(result)
                            }
                        >
                            <ListItemText
                                primary={
                                    result.text ||
                                    '(annotation)'
                                }
                                secondary={
                                    result.canvasId
                                }
                            />
                        </ListItemButton>
                    ))}
                </List>
            )}
        </div>
    );
}


/*
 * ------------------------------------------------------------
 * Sidebar button
 * ------------------------------------------------------------
 */


function LocalStorageSearchButton({
    title = 'Search annotations',
    ...props
}) {
        
    return (
        <svg
            {...props}
            width="28"
            height="28"
            viewBox="0 0 24 24"
            aria-label={title}
            fill="none"
        >
        <circle
            cx="11.5"
            cy="11.5"
            r="9"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
        />
        <line
            x1="18"
            y1="18"
            x2="25.5"
            y2="25.5"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
        />
        </svg>
    );
}

LocalStorageSearchButton.value = 'custom';





/*
 * ------------------------------------------------------------
 * Add the search button
 * ------------------------------------------------------------
 */

const LocalStorageSearchButtonPlugin = {
    target: 'WindowSideBarButtons',
    mode: 'add',
    name: 'LocalStorageSearchButton',
    component: LocalStorageSearchButton,
};

const DebugWindowSideBarButtonsPlugin = {
    target: 'WindowSideBarButtons',
    mode: 'wrap',
    name: 'DebugWindowSideBarButtons',
    component: (props) => {
        console.log(
            'WINDOW SIDEBAR PROPS:',
            props
        );

        const {
            TargetComponent,
            ...rest
        } = props;

        return (
            <TargetComponent
                {...rest}
            />
        );
    },
};

/*
 * ------------------------------------------------------------
 * Replace the contents of Mirador's CustomPanel
 * ------------------------------------------------------------
 */

function LocalStorageCustomPanel({
    targetProps,
    TargetComponent,
}) {
    const {
        windowId,
    } = targetProps;

    return (
        <TargetComponent
            {...targetProps}
            title="Local annotation search"
        >
            <LocalStorageSearch
                windowId={windowId}
            />
        </TargetComponent>
    );
}

const LocalStorageCustomPanelPlugin = {
    target: 'CustomPanel',
    mode: 'wrap',
    name: 'LocalStorageCustomPanel',
    component: LocalStorageCustomPanel,
};


export default [
    LocalStorageSearchButtonPlugin,
    LocalStorageCustomPanelPlugin,
    DebugWindowSideBarButtonsPlugin,

];
